/**
 * Platform API Client — Control Plane Endpoints
 *
 * Typed fetch client for admin execution endpoints.
 * All endpoints require admin JWT (Bearer token).
 * Types match exact backend response shapes from platform.ts routes.
 */

const BASE_URL = import.meta.env.VITE_API_URL || 'https://api-bay-five-33.vercel.app';
const PLATFORM_BASE = `${BASE_URL}/api/v1/platform`;

// ── Auth ────────────────────────────────────────────────────────────────────

export const AUTH_TOKEN_KEY = 'nexum_admin_jwt';
const REFRESH_TOKEN_KEY = 'nexum_refresh_token';

export function getAdminToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAdminToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAdminToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

const BASE = import.meta.env.VITE_API_URL || 'https://api-bay-five-33.vercel.app';

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns true if refresh succeeded, false if session is dead.
 * Deduplicates concurrent refresh attempts.
 */
export async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const rt = getRefreshToken();
    if (!rt) return false;

    try {
      const res = await fetch(`${BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) {
        clearAdminToken();
        return false;
      }
      const data = await res.json();
      setAdminToken(data.access_token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

function adminHeaders(): Record<string, string> {
  const token = getAdminToken();
  if (!token) throw new PlatformApiError('Admin JWT not configured', 401);
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// ── Error ───────────────────────────────────────────────────────────────────

export class PlatformApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'PlatformApiError';
    this.status = status;
  }
  get isRateLimited(): boolean { return this.status === 429; }
  get isAuthError(): boolean { return this.status === 401 || this.status === 403; }
  get isNotFound(): boolean { return this.status === 404; }
  get isServerError(): boolean { return this.status >= 500; }
  /** Operator-friendly error message — no raw HTTP codes or stack traces */
  get operatorMessage(): string {
    if (this.isRateLimited) return 'Temporarily rate-limited. Data will refresh shortly.';
    if (this.isAuthError) return 'Session expired or insufficient permissions. Sign in again.';
    if (this.isNotFound) return 'Resource not found.';
    if (this.isServerError) return 'Server temporarily unavailable. Try refreshing.';
    return this.message;
  }
}

/**
 * Extract a safe, renderable error string from any thrown value.
 * Prevents [object Object] from ever reaching the UI.
 */
export function safeErrorMessage(err: unknown, fallback = 'Something went wrong. Try refreshing.'): string {
  if (err instanceof PlatformApiError) return err.operatorMessage;
  if (err instanceof Error) return err.message || fallback;
  if (typeof err === 'string') return err;
  return fallback;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    let message = `${response.status} ${response.statusText}`;
    try {
      const parsed = JSON.parse(body);
      if (parsed.error) message = parsed.error;
    } catch {
      // use default
    }
    // Auto-clear expired/invalid JWT on 401 so UI reflects reality
    if (response.status === 401 && getAdminToken()) {
      clearAdminToken();
      // Trigger storage event so AuthGate picks up the change
      window.dispatchEvent(new StorageEvent('storage', { key: AUTH_TOKEN_KEY }));
    }
    throw new PlatformApiError(message, response.status);
  }
  return response.json();
}

// ── Shared Kernel Types (match backend exactly) ─────────────────────────────

export type Decision = 'allowed' | 'blocked' | 'failed';
export type ActionPosture = 'executable' | 'blocked' | 'failed' | 'downgradeable';
export type ReasonPhase = 'policy' | 'guard' | 'execution';
export type IntegrityStatus = 'hash_verified' | 'hash_mismatch' | 'unavailable';
export type IntegrityHint = 'hash_present' | 'hash_missing' | 'no_transcript';
export type SigningStatus = 'signed' | 'unsigned' | 'signing_unavailable';
export type PrecursorValidity = 'valid' | 'tainted' | 'incomplete';
export type PrecursorStatus = 'issued' | 'tainted' | 'incomplete';
export type Environment = 'dev' | 'staging';

export type ActionClassCeiling =
  | 'read_only'
  | 'external_read'
  | 'external_write'
  | 'financial_action'
  | 'destructive_action';

export type EffectClass =
  | 'observe' | 'summarize' | 'propose' | 'draft' | 'route'
  | 'escalate' | 'approve_ready' | 'notify' | 'delegate_further'
  | 'record' | 'execute';

// ── Response Types ──────────────────────────────────────────────────────────

/** Summary row returned by GET /admin/executions (list) */
export interface ExecutionSummary {
  execution_id: string;
  object_id: string;
  capability_id: string;
  environment: Environment;
  status: string;
  error_class?: string;
  decision?: Decision;
  reason_code?: string;
  reason_phase?: ReasonPhase | null;
  integrity_hint: IntegrityHint;
  duration_ms: number;
  correlation_id?: string;
  created_at: string;
}

export interface ExecutionListResponse {
  records: ExecutionSummary[];
  total: number;
  limit: number;
  offset: number;
  timestamp: string;
}

/** Blocking dimension from explanation */
export interface BlockingDimension {
  dimension: string;
  phase: 'policy' | 'guard' | 'execution';
  status: 'passed' | 'blocked' | 'skipped';
  reason_code?: string;
  detail?: string;
}

/** Missing condition from explanation */
export interface MissingCondition {
  condition: string;
  current_value?: string;
  required_value?: string;
  remediation?: string;
}

/** Downgrade option from explanation */
export interface DowngradeOption {
  effect: ActionClassCeiling;
  environment: Environment;
  what_changes: string;
}

/** Next safe action from explanation */
export interface NextSafeAction {
  effect: ActionClassCeiling;
  environment: Environment;
  description: string;
}

/** Canonical explanation */
export interface CanonicalExplanation {
  schema_version: string;
  decision: Decision;
  decision_code: string;
  requested_effect: ActionClassCeiling;
  granted_effect: ActionClassCeiling | null;
  effect_class: EffectClass;
  blocking_dimensions: BlockingDimension[];
  missing_conditions: MissingCondition[];
  next_safe_action: NextSafeAction | null;
  downgrade_options: DowngradeOption[];
  environment: Environment;
  enforcement_level: string;
  trust_score: number;
  trust_delta: number;
  risk_tier: number;
  dependency_status: string;
  correlation_id: string;
  decided_at: string;
}

/** Integrity verification result */
export interface IntegrityVerification {
  content_hash: string | null;
  hash_algorithm: 'sha256';
  serialization: string;
  integrity_status: IntegrityStatus;
  unavailable_reason?: string;
  verified_at: string | null;
}

/** Correlation check result */
export interface CorrelationCheck {
  consistent: boolean;
  transcript_correlation: string | null;
  explanation_correlation: string | null;
  row_correlation: string | null;
  mismatch?: string;
}

/** Capability pin (dependency snapshot) */
export interface CapabilityPin {
  capability_id: string;
  capability_version: number;
  contract_hash: string;
  trust_score: number;
  required: boolean;
}

/** Full execution detail */
export interface ExecutionDetail {
  execution: ExecutionSummary;
  explanation: CanonicalExplanation | null;
  transcript: AdjudicationTranscript | null;
  transcript_hash: string | null;
  enforcement_result: EnforcementResult | null;
  dependency_pin_snapshot: CapabilityPin | null;
  integrity: IntegrityVerification;
  correlation_consistency: boolean;
  correlation_check: CorrelationCheck;
  modules_evaluated?: string[] | null;
  risk_assessment?: {
    risk_level: string;
    risk_score: number;
    dimensions: Record<string, number>;
    explanation: string;
    dimension_tags: string[];
    oversight_triggered: boolean;
    domain_pack_slug?: string;
    domain_action_context?: {
      matched_action_classes: { id: string; label: string; mode_ceiling: string; default_oversight: string }[];
      domain_pack_slug: string;
      domain: string;
    };
    domain_document_context?: {
      document_classes: { id: string; label: string; sensitivity_class: string; required_fields: string[] }[];
      domain_pack_slug: string;
      domain: string;
    };
    domain_recommendation_context?: {
      recommendation_weights: { delta_risk: number; delta_evidence: number; delta_admissibility: number; cost_penalty: number; latency_penalty: number };
      domain_pack_slug: string;
      domain: string;
    };
    domain_trust_context?: {
      gamma_success: number;
      gamma_failure: number;
      domain_pack_slug: string;
    };
    domain_eval_context?: {
      dimension_count: number;
      aggregate_thresholds: { fail_below: number; pass_above: number } | null;
      domain_pack_slug: string;
    };
    domain_hard_fail_result?: {
      triggered: boolean;
      triggered_rules: { factor: string; threshold: number; actual_value: number; action: string; reason: string }[];
      domain_pack_slug: string;
    };
  } | null;
}

/** Enforcement result from execution */
export interface EnforcementResult {
  allowed: boolean;
  reason?: string;
  reason_code?: string;
  enforcement_type?: string;
  object_id?: string;
  remaining_budget?: number;
  policy_checks?: PolicyCheck[];
}

/** Individual policy check */
export interface PolicyCheck {
  check: string;
  order: number;
  passed: boolean;
  reason_code?: string;
  detail?: string;
  duration_ms?: number;
}

/** Guard check */
export interface GuardCheck {
  guard: string;
  order: number;
  passed: boolean;
  reason?: string;
  reason_code?: string;
  resolved_ips?: string[];
}

/** Execution constraints */
export interface ExecutionConstraints {
  allowed_domain: string;
  allowed_method: string;
  allowed_headers: string[];
  max_response_bytes: number;
  allowed_content_types: string[];
  timeout_ms: number;
  ssrf_resolved_ips?: string[];
}

// ── Transcript Types ────────────────────────────────────────────────────────

export interface TranscriptPhase {
  phase: string;
  started_at: string;
  outcome: string;
  duration_ms: number;
  checks?: Array<{
    check: string;
    order: number;
    passed: boolean;
    reason_code?: string;
    detail?: string;
    duration_ms?: number;
  }>;
  blocked_at_check?: string;
  constraints_applied?: ExecutionConstraints;
  // Execution phase fields
  status?: string;
  capability_latency_ms?: number;
  response_content_type?: string;
  response_status_code?: number;
  error_class?: string;
  error_message?: string;
  // Trust phase fields
  trust_before?: number;
  trust_after?: number;
  trust_delta_raw?: number;
  lifecycle_cap?: number;
  dependency_ceiling?: number;
  trust_mapped?: number;
  formula?: string;
  gamma_s?: number;
  gamma_f?: number;
}

export interface AdjudicationTranscript {
  schema_version: string;
  transcript_id: string;
  correlation_id: string;
  request: {
    object_id: string;
    object_type: string;
    capability_id: string;
    environment: Environment;
    input_hash: string;
    requested_at: string;
  };
  dependency_snapshot: CapabilityPin;
  policy_phase: TranscriptPhase;
  guard_phase: TranscriptPhase;
  execution_phase: TranscriptPhase;
  trust_phase: TranscriptPhase;
  explanation: CanonicalExplanation;
  created_at: string;
}

// ── Replay Types ────────────────────────────────────────────────────────────

export interface ReplayPhase {
  phase: string;
  outcome: string;
  duration_ms: number;
  details: Record<string, unknown>;
}

export interface ReplayDecision {
  outcome: Decision;
  decision_code: string;
  reason_phase: ReasonPhase | null;
  blocking_dimensions: BlockingDimension[];
  missing_conditions: MissingCondition[];
  next_safe_action: NextSafeAction | null;
  downgrade_options: DowngradeOption[];
}

export interface ReplayEnvelope {
  correlation_id: string;
  schema_version: string;
  requested_at: string;
  decided_at: string;
  total_duration_ms: number;
  request: {
    object_id: string;
    capability_id: string;
    environment: string;
    input_hash: string;
  };
  phases: ReplayPhase[];
  decision: ReplayDecision;
  integrity: IntegrityVerification;
}

export interface ReplayResponse {
  replay: ReplayEnvelope | null;
  reason?: string;
}

// ── Precursor Types ─────────────────────────────────────────────────────────

export interface PrecursorDecision {
  outcome: Decision;
  decision_code: string;
  reason_phase: ReasonPhase | null;
  blocking_dimensions: BlockingDimension[];
  missing_conditions: MissingCondition[];
  next_safe_action: NextSafeAction | null;
  downgrade_options: DowngradeOption[];
  downgrade_strategy: 'none' | 'environment' | 'effect_class';
}

export interface PrecursorAuthority {
  requested_effect: ActionClassCeiling;
  granted_effect: ActionClassCeiling | null;
  effect_class: EffectClass;
  environment: Environment;
}

export interface PrecursorTrust {
  trust_before: number;
  trust_after: number;
  trust_delta: number;
  formula: string;
}

export interface PrecursorEnforcement {
  policy_outcome: string;
  guard_outcome: string;
  enforcement_level: string;
  isolation_enforced: boolean;
}

export interface PrecursorIntegrity {
  integrity_status: IntegrityStatus;
  content_hash: string | null;
  correlation: CorrelationCheck;
}

export interface AgentActionSummary {
  posture: ActionPosture;
  retryable: boolean;
  alternative_available: boolean;
  reason_code: string;
  decided_in_phase: ReasonPhase | null;
  requested_authority: ActionClassCeiling;
  granted_authority: ActionClassCeiling | null;
  trust_at_decision: number;
  suggested_alternative: { effect: ActionClassCeiling; environment: Environment; description: string } | null;
  dependency_status: string;
  environment: Environment;
}

export interface AlgorithmManifest {
  governing_algorithms: string[];
  registry_size: number;
  bound_action_fields: number;
  total_action_fields: number;
}

export interface ProtocolPrecursor {
  precursor_version: string;
  correlation_id: string;
  validity: PrecursorValidity;
  status: PrecursorStatus;
  deployment_mode: string;
  decision: PrecursorDecision;
  authority: PrecursorAuthority;
  trust: PrecursorTrust;
  enforcement: PrecursorEnforcement;
  integrity: PrecursorIntegrity;
  action_summary: AgentActionSummary;
  precursor_hash: string;
  algorithm_manifest: AlgorithmManifest;
  assembled_at: string;
}

export interface PrecursorResponse {
  precursor: ProtocolPrecursor | null;
  signing?: {
    status: SigningStatus;
    jws: string | null;
  };
  reason?: string;
}

// ── Stats Types ─────────────────────────────────────────────────────────────

export interface DecisionDistribution {
  allowed: number;
  blocked: number;
  failed: number;
  total: number;
}

export interface DecisionStatsResponse {
  distribution: DecisionDistribution;
  total: number;
  period: { from: string | null; to: string | null };
  timestamp: string;
}

export interface BlockReasonEntry {
  reason_code: string;
  count: number;
  phase: string;
  latest_at: string;
}

export interface BlockReasonsResponse {
  block_reasons: BlockReasonEntry[];
  total_blocked: number;
  period: { from: string | null; to: string | null };
  timestamp: string;
}

export interface TrustHistoryEntry {
  execution_id: string;
  status: string;
  decision?: Decision;
  trust_before?: number;
  trust_after?: number;
  trust_delta?: number;
  formula?: string;
  lifecycle_cap?: number;
  dependency_ceiling?: number;
  created_at: string;
}

export interface TrustHistoryResponse {
  object_id: string;
  trust_history: TrustHistoryEntry[];
  current_trust: number;
  total_executions: number;
  timestamp: string;
}

// ── API Methods ─────────────────────────────────────────────────────────────

export interface ExecutionFilters {
  object_id?: string;
  capability_id?: string;
  status?: string;
  environment?: string;
  correlation_id?: string;
  decision?: Decision;
  reason_code?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

/** List executions with filtering */
export async function listExecutions(filters: ExecutionFilters = {}): Promise<ExecutionListResponse> {
  const params = new URLSearchParams();
  if (filters.object_id) params.set('object_id', filters.object_id);
  if (filters.capability_id) params.set('capability_id', filters.capability_id);
  if (filters.status) params.set('status', filters.status);
  if (filters.environment) params.set('environment', filters.environment);
  if (filters.correlation_id) params.set('correlation_id', filters.correlation_id);
  if (filters.decision) params.set('decision', filters.decision);
  if (filters.reason_code) params.set('reason_code', filters.reason_code);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  params.set('limit', String(filters.limit || 20));
  params.set('offset', String(filters.offset || 0));

  const response = await fetch(`${PLATFORM_BASE}/admin/executions?${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<ExecutionListResponse>(response);
}

/** Get full execution detail */
export async function getExecutionDetail(executionId: string): Promise<ExecutionDetail> {
  const response = await fetch(`${PLATFORM_BASE}/admin/executions/${encodeURIComponent(executionId)}`, {
    headers: adminHeaders(),
  });
  return handleResponse<ExecutionDetail>(response);
}

/** Get execution by correlation ID */
export async function getExecutionByCorrelation(correlationId: string): Promise<ExecutionDetail> {
  const response = await fetch(`${PLATFORM_BASE}/admin/executions/by-correlation/${encodeURIComponent(correlationId)}`, {
    headers: adminHeaders(),
  });
  return handleResponse<ExecutionDetail>(response);
}

/** Get replay envelope */
export async function getExecutionReplay(executionId: string): Promise<ReplayResponse> {
  const response = await fetch(`${PLATFORM_BASE}/admin/executions/replay/${encodeURIComponent(executionId)}`, {
    headers: adminHeaders(),
  });
  return handleResponse<ReplayResponse>(response);
}

/** Get protocol precursor (optionally signed) */
export async function getExecutionPrecursor(executionId: string, signed = false): Promise<PrecursorResponse> {
  const params = signed ? '?signed=true' : '';
  const response = await fetch(`${PLATFORM_BASE}/admin/executions/precursor/${encodeURIComponent(executionId)}${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<PrecursorResponse>(response);
}

/** Get precursor by correlation ID */
export async function getPrecursorByCorrelation(correlationId: string, signed = false): Promise<PrecursorResponse> {
  const params = signed ? '?signed=true' : '';
  const response = await fetch(`${PLATFORM_BASE}/admin/executions/precursor-by-correlation/${encodeURIComponent(correlationId)}${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<PrecursorResponse>(response);
}

/** Get decision distribution stats */
export async function getDecisionStats(filters: { object_id?: string; from?: string; to?: string } = {}): Promise<DecisionStatsResponse> {
  const params = new URLSearchParams();
  if (filters.object_id) params.set('object_id', filters.object_id);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);

  const response = await fetch(`${PLATFORM_BASE}/admin/executions/stats/decisions?${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<DecisionStatsResponse>(response);
}

/** Get block reason aggregation */
export async function getBlockReasons(filters: { object_id?: string; from?: string; to?: string; limit?: number } = {}): Promise<BlockReasonsResponse> {
  const params = new URLSearchParams();
  if (filters.object_id) params.set('object_id', filters.object_id);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.limit) params.set('limit', String(filters.limit));

  const response = await fetch(`${PLATFORM_BASE}/admin/executions/stats/block-reasons?${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<BlockReasonsResponse>(response);
}

/** Get trust history for a platform object */
export async function getTrustHistory(objectId: string, filters: { from?: string; to?: string; limit?: number } = {}): Promise<TrustHistoryResponse> {
  const params = new URLSearchParams();
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.limit) params.set('limit', String(filters.limit));

  const response = await fetch(`${PLATFORM_BASE}/admin/executions/stats/trust-history/${encodeURIComponent(objectId)}?${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<TrustHistoryResponse>(response);
}

// ── Platform Object Types ───────────────────────────────────────────────────

export interface PlatformObject {
  object_id: string;
  object_type: 'skill' | 'workflow' | 'agent';
  name: string;
  description: string;
  version: number;
  lifecycle_state: string;
  risk_class: string;
  trust_score: number;
  execution_scope: string;
  dependency_status: string;
  submitted_at: string;
  evaluated_at: string | null;
}

export interface PlatformObjectDetail extends PlatformObject {
  dependency_pins: CapabilityPin[] | null;
  permissions_granted: string[] | null;
  contract_hash: string | null;
  admitted_at: string | null;
  invalidated_at: string | null;
  invalidation_reason: string | null;
  risk_tier: number | null;
  isolation_profile: string | null;
  activation_status: string | null;
  activation_blocked_reason: string | null;
  environment_eligibility: Record<string, boolean> | null;
  action_class_ceiling: string | null;
  enforcement_level: string | null;
  isolation_enforced: boolean | null;
  last_dependency_check: string | null;
  stale_since: string | null;
}

export interface PlatformObjectListResponse {
  objects: PlatformObject[];
  total: number;
  limit: number;
  offset: number;
}

export interface PlatformObjectDetailResponse {
  object: PlatformObjectDetail;
  evaluations: Array<{
    evaluation_type: string;
    passed: boolean;
    score: number | null;
    details: Record<string, unknown> | null;
    evaluated_at: string;
  }>;
  runtime_bindings: Array<{
    binding_type: string;
    policy: Record<string, unknown> | null;
    active: boolean;
  }>;
}

// ── Platform Object API Methods ─────────────────────────────────────────────

/** List platform objects with optional filters */
export async function listPlatformObjects(filters: {
  type?: string;
  lifecycle?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<PlatformObjectListResponse> {
  const params = new URLSearchParams();
  if (filters.type) params.set('type', filters.type);
  if (filters.lifecycle) params.set('lifecycle', filters.lifecycle);
  params.set('limit', String(filters.limit || 50));
  params.set('offset', String(filters.offset || 0));

  const response = await fetch(`${PLATFORM_BASE}?${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<PlatformObjectListResponse>(response);
}

/** Get platform object detail */
export async function getPlatformObject(objectId: string): Promise<PlatformObjectDetailResponse> {
  const response = await fetch(`${PLATFORM_BASE}/${encodeURIComponent(objectId)}`, {
    headers: adminHeaders(),
  });
  return handleResponse<PlatformObjectDetailResponse>(response);
}

/** Suspend a platform object (kill switch) */
export async function suspendObject(objectId: string, reason: string): Promise<{ status: string; object_id: string; reason: string }> {
  const response = await fetch(`${PLATFORM_BASE}/${encodeURIComponent(objectId)}/suspend`, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify({ reason }),
  });
  return handleResponse<{ status: string; object_id: string; reason: string }>(response);
}

// ── Evaluate + Activate Types ───────────────────────────────────────────────

export interface EvaluationStep {
  step: number;
  name: string;
  passed: boolean;
}

export interface EvaluationResult {
  object_id: string;
  evaluation: {
    steps: EvaluationStep[];
    risk_class: string;
    execution_scope: string;
    policies_attached: number;
    dependency_health: string;
    overall_passed: boolean;
  };
}

export interface ActivationGate {
  gate: string;
  passed: boolean;
  reason: string;
}

export interface ActivationResult {
  object_id: string;
  risk_tier: number;
  isolation_profile: string;
  activation_status: string;
  lifecycle_state: string;
  fast_path: boolean;
  gates: ActivationGate[];
  blocking_reasons: string[];
}

/** Trigger sandbox evaluation (admin, steps 7-12) */
export async function evaluatePlatformObject(objectId: string): Promise<EvaluationResult> {
  const response = await fetch(`${PLATFORM_BASE}/${encodeURIComponent(objectId)}/evaluate`, {
    method: 'POST',
    headers: adminHeaders(),
  });
  return handleResponse<EvaluationResult>(response);
}

/** Run activation pipeline (admin) */
export async function activatePlatformObject(objectId: string): Promise<ActivationResult> {
  const response = await fetch(`${PLATFORM_BASE}/${encodeURIComponent(objectId)}/activate`, {
    method: 'POST',
    headers: adminHeaders(),
  });
  return handleResponse<ActivationResult>(response);
}

// ── Learning / Recommendation Types ─────────────────────────────────────────

export type RecommendationReviewStatus =
  | 'candidate' | 'pending_review' | 'approved' | 'rejected' | 'superseded' | 'expired';

export type RecommendationPriority = 'critical' | 'high' | 'medium' | 'low';

export type ReviewDecisionType = 'approve' | 'reject';

export type RecommendationRejectionReason =
  | 'insufficient_evidence' | 'wrong_scope' | 'already_addressed'
  | 'risk_too_high' | 'deferred' | 'disagree_with_action' | 'other';

export type DriftDirection = 'improving' | 'degrading' | 'stable';
export type DriftSeverity = 'minor' | 'moderate' | 'severe';
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface RecommendationListItem {
  candidate_id: string;
  status: RecommendationReviewStatus;
  action: string;
  confidence: string;
  cohort_key: string;
  primary_metric: string;
  primary_metric_value: number;
  priority: RecommendationPriority;
  generating_rule_id: string;
  generated_at: string;
  expires_at: string | null;
  tenant_id: string | null;
  _classification: 'inferred_recommendation';
}

export interface RecommendationListResponse {
  recommendations: RecommendationListItem[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  _classification: 'inferred_recommendation';
}

export interface RecommendationEvidenceView {
  metric_name: string;
  metric_value: number;
  threshold: number;
  rule_id: string;
  sample_size: number;
  drift_direction: DriftDirection | null;
  drift_severity: DriftSeverity | null;
  _classification: 'derived_metric';
}

export interface RecommendationMetricsSnapshot {
  sample_size: number;
  sample_sufficient: boolean;
  false_allow_rate: number;
  false_block_rate: number;
  correct_allow_rate: number;
  oversight_positive_rate: number;
  capability_instability_rate: number;
  trust_collapse_rate: number;
  risk_level: RiskLevel;
  _classification: 'derived_metric';
}

export interface RecommendationDecisionContext {
  priority: RecommendationPriority;
  urgency_reason: string;
  staleness_hours: number;
  is_stale: boolean;
  risk_level: RiskLevel;
  evidence_count: number;
  sample_sufficient: boolean;
}

export interface RecommendationCardSummary extends RecommendationListItem {
  rationale?: string;
}

export interface RecommendationDetailResponse {
  candidate: RecommendationCardSummary;
  decision_context: RecommendationDecisionContext;
  evidence: RecommendationEvidenceView[];
  scope: {
    cohort_dimensions: Record<string, string>;
    subject_types: string[];
    tenant_id: string | null;
    _classification: 'observed_fact';
  };
  rationale: string;
  disclaimer: string;
  algorithm_version: string;
  superseded_by: string | null;
  _classifications: Record<string, string>;
}

export interface RecommendationQueueSummary {
  total_pending: number;
  by_status: Record<string, number>;
  by_action: Record<string, number>;
  by_priority: Record<RecommendationPriority, number>;
  stale_count: number;
  computed_at: string;
  _classification: 'derived_metric';
}

export interface RecommendationDecisionResponse {
  review_id: string;
  candidate_id: string;
  new_status: RecommendationReviewStatus;
  decision: ReviewDecisionType;
  decided_at: string;
  advisory_notice: string;
  _classification: 'operator_decision';
}

export interface RecommendationAuditEntry {
  event_id: string;
  event_type: string;
  previous_status: RecommendationReviewStatus | null;
  new_status: RecommendationReviewStatus;
  actor: string;
  occurred_at: string;
  metadata: Record<string, unknown>;
}

export interface RecommendationAuditResponse {
  candidate_id: string;
  events: RecommendationAuditEntry[];
  total_events: number;
}

// ── Application / Efficacy / Supersession Types ────────────────────────────
// Match bridge-contracts.ts and read-models.ts. API routes are now mounted
// at GET .../recommendations/:id/bridge, /efficacy, /supersession-chain.

export type RecommendationApplicationState =
  | 'approved_pending_application' | 'applied' | 'monitoring' | 'rolled_back' | 'closed';

export type RecommendationImpact =
  | 'strongly_positive' | 'moderately_positive' | 'neutral'
  | 'moderately_negative' | 'strongly_negative' | 'insufficient_data';

export type SupersessionReason =
  | 'newer_calibration' | 'scope_refinement' | 'contradicting_evidence' | 'manual_replacement';

/**
 * Derive the operator-facing application state from the review status.
 * This is a UI-local derivation — the backend does not yet expose
 * application state via API. Pure, deterministic.
 */
export function deriveApplicationState(
  reviewStatus: RecommendationReviewStatus,
  supersededBy: string | null,
): {
  applicationState: RecommendationApplicationState | 'not_applicable';
  label: string;
  actionable: boolean;
} {
  if (reviewStatus === 'approved' && !supersededBy) {
    return { applicationState: 'approved_pending_application', label: 'Approved — Pending Application', actionable: false };
  }
  if (reviewStatus === 'approved' && supersededBy) {
    return { applicationState: 'approved_pending_application', label: 'Approved — Superseded Before Application', actionable: false };
  }
  if (reviewStatus === 'superseded') {
    return { applicationState: 'not_applicable', label: 'Superseded', actionable: false };
  }
  if (reviewStatus === 'rejected') {
    return { applicationState: 'not_applicable', label: 'Rejected', actionable: false };
  }
  if (reviewStatus === 'expired') {
    return { applicationState: 'not_applicable', label: 'Expired', actionable: false };
  }
  // candidate or pending_review — not yet decided
  return { applicationState: 'not_applicable', label: 'Awaiting Review', actionable: false };
}

// ── Learning API Methods ────────────────────────────────────────────────────

const LEARNING_BASE = `${BASE_URL}/api/v1/learning`;

export interface RecommendationFilters {
  status?: string;
  action?: string;
  priority?: RecommendationPriority;
  cohort_key?: string;
  sort?: 'priority' | 'generated_at';
  limit?: number;
  offset?: number;
}

/** List recommendation candidates with filters + priority sort */
export async function listRecommendations(filters: RecommendationFilters = {}): Promise<RecommendationListResponse> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.action) params.set('action', filters.action);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.cohort_key) params.set('cohort_key', filters.cohort_key);
  if (filters.sort) params.set('sort', filters.sort);
  params.set('limit', String(filters.limit || 50));
  params.set('offset', String(filters.offset || 0));

  const response = await fetch(`${LEARNING_BASE}/recommendations?${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<RecommendationListResponse>(response);
}

/** Get queue summary (counts by status, action, priority) */
export async function getRecommendationQueueSummary(): Promise<RecommendationQueueSummary> {
  const response = await fetch(`${LEARNING_BASE}/recommendations/pending/summary`, {
    headers: adminHeaders(),
  });
  return handleResponse<RecommendationQueueSummary>(response);
}

/** Get recommendation detail with evidence and decision context */
export async function getRecommendationDetail(candidateId: string): Promise<RecommendationDetailResponse> {
  const response = await fetch(`${LEARNING_BASE}/recommendations/${encodeURIComponent(candidateId)}`, {
    headers: adminHeaders(),
  });
  return handleResponse<RecommendationDetailResponse>(response);
}

/** Submit approve/reject decision */
export async function decideRecommendation(
  candidateId: string,
  decision: ReviewDecisionType,
  rejectionReason?: RecommendationRejectionReason,
  notes?: string,
): Promise<RecommendationDecisionResponse> {
  const body: Record<string, unknown> = { decision };
  if (rejectionReason) body.rejection_reason = rejectionReason;
  if (notes) body.notes = notes;

  const response = await fetch(`${LEARNING_BASE}/recommendations/${encodeURIComponent(candidateId)}/decide`, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse<RecommendationDecisionResponse>(response);
}

/** Queue a candidate for review */
export async function queueRecommendation(candidateId: string): Promise<{ candidate_id: string; new_status: string }> {
  const response = await fetch(`${LEARNING_BASE}/recommendations/${encodeURIComponent(candidateId)}/queue`, {
    method: 'POST',
    headers: adminHeaders(),
  });
  return handleResponse<{ candidate_id: string; new_status: string }>(response);
}

/** Get audit trail for a recommendation */
export async function getRecommendationAudit(candidateId: string): Promise<RecommendationAuditResponse> {
  const response = await fetch(`${LEARNING_BASE}/recommendations/${encodeURIComponent(candidateId)}/audit`, {
    headers: adminHeaders(),
  });
  return handleResponse<RecommendationAuditResponse>(response);
}

// ── Bridge / Efficacy / Supersession API Methods ────────────────────────────
// These consume the real mounted endpoints from routes/learning.ts.

/** Bridge summary from read-models.ts */
export interface RecommendationBridgeSummary {
  candidate_id: string;
  bridge_id: string | null;
  application_state: RecommendationApplicationState | 'no_bridge';
  readiness: string;
  approved_at: string | null;
  applied_at: string | null;
  monitoring_active: boolean;
  efficacy_measured: boolean;
  days_since_approval: number | null;
  tenant_id: string | null;
  _classification: 'derived_metric';
}

/** Efficacy measurement response */
export interface RecommendationEfficacyResponse {
  candidate_id: string;
  measurement_eligible: boolean;
  measurement_status: string;
  reason: string;
  pre_count: number;
  post_count: number;
  efficacy_score: number | null;
  impact: RecommendationImpact | null;
  _classification: 'derived_metric';
}

/** Supersession chain entry (from supersession-visibility.ts) */
export interface SupersessionChainEntry {
  candidate_id: string;
  status: RecommendationReviewStatus;
  action: string;
  generated_at: string;
  superseded_by: string | null;
  is_current_head: boolean;
}

/** Supersession chain view */
export interface RecommendationSupersessionChainView {
  cohort_key: string;
  current_head_id: string | null;
  chain_length: number;
  superseded_count: number;
  strength: string;
  chain: SupersessionChainEntry[];
  tenant_id: string | null;
  _classification: 'derived_metric';
}

/** Get bridge/application summary */
export async function getRecommendationBridge(candidateId: string): Promise<RecommendationBridgeSummary> {
  const response = await fetch(`${LEARNING_BASE}/recommendations/${encodeURIComponent(candidateId)}/bridge`, {
    headers: adminHeaders(),
  });
  return handleResponse<RecommendationBridgeSummary>(response);
}

/** Get efficacy measurement */
export async function getRecommendationEfficacy(candidateId: string): Promise<RecommendationEfficacyResponse> {
  const response = await fetch(`${LEARNING_BASE}/recommendations/${encodeURIComponent(candidateId)}/efficacy`, {
    headers: adminHeaders(),
  });
  return handleResponse<RecommendationEfficacyResponse>(response);
}

/** Get supersession chain */
export async function getRecommendationSupersessionChain(candidateId: string): Promise<RecommendationSupersessionChainView> {
  const response = await fetch(`${LEARNING_BASE}/recommendations/${encodeURIComponent(candidateId)}/supersession-chain`, {
    headers: adminHeaders(),
  });
  return handleResponse<RecommendationSupersessionChainView>(response);
}

// ── Apply Workflow + Application Audit ──────────────────────────────────────

export type ApplicationTarget = 'policy_declaration' | 'oversight_rule' | 'trust_parameter' | 'enforcement_threshold' | 'none';
export type MonitoringState = 'not_started' | 'active' | 'complete' | 'cancelled';

export interface ApplyRecommendationRequest {
  target_system: ApplicationTarget;
  target_parameter: string;
  current_value?: string;
  proposed_value: string;
  reversible?: boolean;
  notes?: string;
}

export interface ApplyRecommendationResponse {
  bridge_id: string;
  candidate_id: string;
  application_state: RecommendationApplicationState;
  monitoring_start: string | null;
  monitoring_end: string | null;
  advisory_notice: string;
  _classification: 'operator_decision';
}

export interface ApplicationAuditBridge {
  bridge_id: string;
  application_state: RecommendationApplicationState;
  applied_at: string | null;
  applied_by: string | null;
  monitoring_start: string | null;
  monitoring_end: string | null;
  monitoring_state: MonitoringState;
}

export interface ApplicationAuditEfficacy {
  efficacy_score: number;
  impact: RecommendationImpact;
  target_achieved: boolean;
  computed_at: string;
}

export interface ApplicationAuditResponse {
  candidate_id: string;
  bridge: ApplicationAuditBridge | null;
  efficacy_measurements: number;
  latest_efficacy: ApplicationAuditEfficacy | null;
  _classification: 'derived_metric';
}

/** Apply an approved recommendation (creates persisted bridge) */
export async function applyRecommendation(
  candidateId: string,
  request: ApplyRecommendationRequest,
): Promise<ApplyRecommendationResponse> {
  const response = await fetch(`${LEARNING_BASE}/recommendations/${encodeURIComponent(candidateId)}/apply`, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse<ApplyRecommendationResponse>(response);
}

/** Get application audit (persisted bridge + efficacy history) */
export async function getApplicationAudit(candidateId: string): Promise<ApplicationAuditResponse> {
  const response = await fetch(`${LEARNING_BASE}/recommendations/${encodeURIComponent(candidateId)}/application-audit`, {
    headers: adminHeaders(),
  });
  return handleResponse<ApplicationAuditResponse>(response);
}

// ── Replay / Support Types ──────────────────────────────────────────────────
// Mirrors replay-support.ts taxonomy. Pure client-side derivation from
// existing execution data. No new API endpoints — derived locally.

export const RUNTIME_EVENT_CATEGORIES = [
  'execution', 'enforcement', 'oversight', 'credential',
  'degradation', 'invalidation', 'trust_change',
] as const;
export type RuntimeEventCategory = typeof RUNTIME_EVENT_CATEGORIES[number];

export const RUNTIME_EVENT_SEVERITIES = ['info', 'warning', 'error', 'critical'] as const;
export type RuntimeEventSeverity = typeof RUNTIME_EVENT_SEVERITIES[number];

export interface RuntimeReplayRecord {
  event_id: string;
  correlation_id: string;
  category: RuntimeEventCategory;
  severity: RuntimeEventSeverity;
  status: string;
  reason_code: string | null;
  decided_in_phase: string | null;
  object_id: string;
  capability_id: string;
  environment: string;
  requested_effect: string | null;
  granted_effect: string | null;
  trust_score: number | null;
  trust_delta: number | null;
  duration_ms: number | null;
  oversight_request_id: string | null;
  degradation_mode: string | null;
  invalidation_state: string | null;
  invalidation_trigger: string | null;
  monitoring_bridge_id: string | null;
  occurred_at: string;
}

export interface ReplayCorrelationProjection {
  correlation_id: string;
  object_id: string;
  capability_id: string;
  environment: string;
  event_count: number;
  categories: RuntimeEventCategory[];
  max_severity: RuntimeEventSeverity;
  has_invalidation: boolean;
  has_oversight: boolean;
  has_degradation: boolean;
}

/**
 * Derive a RuntimeReplayRecord from an ExecutionSummary.
 * Pure, deterministic. Mirrors replay-support.ts classification logic.
 * UI labels this as "derived from execution record."
 */
export function deriveReplayRecord(exec: ExecutionSummary): RuntimeReplayRecord {
  const rc = exec.reason_code || null;
  const status = exec.status;

  // Category classification (mirrors classifyRuntimeEventCategory)
  let category: RuntimeEventCategory = 'execution';
  if (rc) {
    if (rc.startsWith('credential_')) category = 'credential';
    else if (rc === 'oversight_denied') category = 'oversight';
    else if (rc === 'policy_deny') category = 'enforcement';
    else if (rc.startsWith('dependency_') || rc === 'contract_hash_mismatch' || rc === 'version_drift') category = 'invalidation';
  }
  if (status === 'blocked' && category === 'execution') category = 'enforcement';

  // Severity classification (mirrors classifyRuntimeEventSeverity)
  let severity: RuntimeEventSeverity = 'info';
  if (rc === 'credential_revoked' || rc === 'policy_deny' || rc === 'oversight_denied') severity = 'critical';
  else if (rc?.startsWith('credential_')) severity = 'error';
  else if (rc?.startsWith('dependency_') || rc === 'contract_hash_mismatch') severity = 'warning';
  else if (status === 'failed' || status === 'timed_out') severity = 'error';
  else if (status === 'blocked') severity = 'warning';

  return {
    event_id: exec.execution_id,
    correlation_id: exec.correlation_id || exec.execution_id,
    category,
    severity,
    status,
    reason_code: rc,
    decided_in_phase: exec.reason_phase || null,
    object_id: exec.object_id,
    capability_id: exec.capability_id,
    environment: exec.environment,
    requested_effect: null,
    granted_effect: null,
    trust_score: null,
    trust_delta: null,
    duration_ms: exec.duration_ms,
    oversight_request_id: null,
    degradation_mode: null,
    invalidation_state: null,
    invalidation_trigger: null,
    monitoring_bridge_id: null,
    occurred_at: exec.created_at,
  };
}

/**
 * Build a correlation projection from replay records.
 * Mirrors buildReplayCorrelationProjection.
 */
export function buildCorrelationProjection(
  records: RuntimeReplayRecord[],
): ReplayCorrelationProjection | null {
  if (records.length === 0) return null;
  const first = records[0];
  const categories = [...new Set(records.map(r => r.category))];
  const severityOrder: RuntimeEventSeverity[] = ['info', 'warning', 'error', 'critical'];
  const maxSeverity = records.reduce<RuntimeEventSeverity>((max, r) =>
    severityOrder.indexOf(r.severity) > severityOrder.indexOf(max) ? r.severity : max, 'info');

  return {
    correlation_id: first.correlation_id,
    object_id: first.object_id,
    capability_id: first.capability_id,
    environment: first.environment,
    event_count: records.length,
    categories,
    max_severity: maxSeverity,
    has_invalidation: records.some(r => r.category === 'invalidation'),
    has_oversight: records.some(r => r.category === 'oversight'),
    has_degradation: records.some(r => r.category === 'degradation'),
  };
}

// ── Trust / Protection / Verification Types ────────────────────────────────
// Mirrors trust-protection.ts. No API routes — derived client-side
// from existing integrity/signing/export data.

export const TRUST_PROFILE_LEVELS = ['hash_linked', 'signed', 'unsigned'] as const;
export type TrustProfileLevel = typeof TRUST_PROFILE_LEVELS[number];

export const REDACTION_LEVELS = ['full', 'safe', 'minimal'] as const;
export type RedactionLevel = typeof REDACTION_LEVELS[number];

export const EXPORT_VISIBILITY_LEVELS = ['internal', 'audit', 'external'] as const;
export type ExportVisibility = typeof EXPORT_VISIBILITY_LEVELS[number];

export interface RuntimeTrustProfile {
  trust_level: TrustProfileLevel;
  has_content_hash: boolean;
  has_transcript_hash: boolean;
  has_signature: boolean;
  signing_available: boolean;
  integrity_verified: boolean;
  redaction_level: RedactionLevel;
  export_visibility: ExportVisibility;
}

export interface RuntimeVerificationProjection {
  verification_type: 'hash_check' | 'signature_check' | 'none';
  hash_available: boolean;
  signing_configured: boolean;
  integrity_checked: boolean;
  trust_level: TrustProfileLevel;
}

export interface ExportProtectionSummary {
  total_records: number;
  redaction_level: RedactionLevel;
  export_visibility: ExportVisibility;
  all_safe: boolean;
  unsafe_count: number;
  has_signed_artifacts: boolean;
  has_hash_linked_artifacts: boolean;
  signing_available: boolean;
}

/**
 * Derive trust profile from existing execution/precursor data.
 * Mirrors classifyRuntimeObjectTrustProfile. Pure, deterministic.
 */
export function deriveTrustProfile(
  integrityStatus: IntegrityStatus | undefined,
  signingStatus: SigningStatus | undefined,
  transcriptHash: string | null | undefined,
  contentHash: string | null | undefined,
  redactionLevel: RedactionLevel = 'safe',
  exportVisibility: ExportVisibility = 'audit',
): RuntimeTrustProfile {
  const hasContentHash = !!contentHash;
  const hasTranscriptHash = !!transcriptHash;
  const hasSigned = signingStatus === 'signed';
  const signingAvailable = signingStatus !== 'signing_unavailable';
  const integrityVerified = integrityStatus === 'hash_verified';

  let trustLevel: TrustProfileLevel;
  if (hasSigned) trustLevel = 'signed';
  else if (hasContentHash || hasTranscriptHash) trustLevel = 'hash_linked';
  else trustLevel = 'unsigned';

  return {
    trust_level: trustLevel,
    has_content_hash: hasContentHash,
    has_transcript_hash: hasTranscriptHash,
    has_signature: hasSigned,
    signing_available: signingAvailable,
    integrity_verified: integrityVerified,
    redaction_level: redactionLevel,
    export_visibility: exportVisibility,
  };
}

/**
 * Derive verification readiness from existing data.
 * Mirrors buildRuntimeVerificationProjection.
 */
export function deriveVerificationProjection(
  integrityStatus: IntegrityStatus | undefined,
  signingStatus: SigningStatus | undefined,
  contentHash: string | null | undefined,
): RuntimeVerificationProjection {
  const hasHash = !!contentHash;
  const integrityChecked = integrityStatus === 'hash_verified' || integrityStatus === 'hash_mismatch';
  const signingConfigured = signingStatus !== 'signing_unavailable' && signingStatus !== undefined;
  const hasSigned = signingStatus === 'signed';

  let verificationType: 'hash_check' | 'signature_check' | 'none';
  if (hasSigned) verificationType = 'signature_check';
  else if (hasHash) verificationType = 'hash_check';
  else verificationType = 'none';

  let trustLevel: TrustProfileLevel;
  if (hasSigned) trustLevel = 'signed';
  else if (hasHash) trustLevel = 'hash_linked';
  else trustLevel = 'unsigned';

  return { verification_type: verificationType, hash_available: hasHash, signing_configured: signingConfigured, integrity_checked: integrityChecked, trust_level: trustLevel };
}

// ── Runtime Profile / Integration Types ─────────────────────────────────────
// Mirrors runtime-profile.ts. No API routes — derived client-side.

export const RUNTIME_PROFILES = [
  'governed_execution', 'governed_restricted', 'governed_oversight',
  'governed_blocked', 'unexecuted',
] as const;
export type RuntimeProfile = typeof RUNTIME_PROFILES[number];

export const ISOLATION_LEVELS = ['none', 'modeled', 'enforced'] as const;
export type IsolationLevel = typeof ISOLATION_LEVELS[number];

export const VISIBILITY_SCOPES = [
  'full_admin', 'operator_safe', 'agent_summary', 'external_minimal',
] as const;
export type VisibilityScope = typeof VISIBILITY_SCOPES[number];

export interface ExecutionRoutingProjection {
  deployment_mode: string;
  environment: Environment;
  environment_allowed: boolean;
  environment_ceiling: ActionClassCeiling;
  enforcement_level: string;
  isolation_level: IsolationLevel;
  runtime_profile: RuntimeProfile;
  degradation_active: boolean;
  effective_ceiling: ActionClassCeiling;
}

export interface RuntimeBoundarySummary {
  deployment_mode: string;
  allowed_environments: Environment[];
  production_gated: boolean;
  policy_chain_active: boolean;
  guard_chain_active: boolean;
  trust_computation_active: boolean;
  credential_injection_supported: boolean;
  oversight_gating_supported: boolean;
  degradation_handling_supported: boolean;
  signing_available: boolean;
  isolation_status: IsolationLevel;
}

/**
 * Derive runtime profile from execution status.
 * Mirrors classifyExecutionRuntimeProfile.
 */
export function deriveRuntimeProfile(
  status: string,
  blocked: boolean,
  degradationCeiling: string | null,
  oversightPending: boolean,
): RuntimeProfile {
  if (oversightPending) return 'governed_oversight';
  if (blocked && status === 'blocked') return 'governed_blocked';
  if (degradationCeiling) return 'governed_restricted';
  if (status === 'pending_oversight') return 'governed_oversight';
  if (status === 'completed' || status === 'failed' || status === 'timed_out') return 'governed_execution';
  return 'unexecuted';
}

/**
 * Derive execution routing projection from execution detail.
 * Mirrors buildExecutionRoutingProjection.
 */
export function deriveRoutingProjection(
  environment: string,
  enforcementLevel: string,
  status: string,
  requestedCeiling: ActionClassCeiling | null,
): ExecutionRoutingProjection {
  const env = (['dev', 'staging'].includes(environment) ? environment : 'dev') as Environment;
  const envCeiling: ActionClassCeiling = env === 'dev' ? 'destructive_action' : 'external_write';

  const profile = deriveRuntimeProfile(status, status === 'blocked', null, status === 'pending_oversight');
  const isolationLevel: IsolationLevel = enforcementLevel === 'permission_enforced' ? 'modeled' : 'none';

  return {
    deployment_mode: 'protocol_only',
    environment: env,
    environment_allowed: true,
    environment_ceiling: envCeiling,
    enforcement_level: enforcementLevel,
    isolation_level: isolationLevel,
    runtime_profile: profile,
    degradation_active: false,
    effective_ceiling: requestedCeiling || envCeiling,
  };
}

/**
 * Build static runtime boundary summary.
 * Represents current Protocol posture — mostly constant.
 */
export function buildBoundarySummary(): RuntimeBoundarySummary {
  return {
    deployment_mode: 'protocol_only',
    allowed_environments: ['dev', 'staging'],
    production_gated: true,
    policy_chain_active: true,
    guard_chain_active: true,
    trust_computation_active: true,
    credential_injection_supported: true,
    oversight_gating_supported: true,
    degradation_handling_supported: true,
    signing_available: false, // unknown client-side — derived from precursor when available
    isolation_status: 'modeled',
  };
}

// ── Wave 3: Agent / Workflow / Tool Contracts ───────────────────────────────
// Mirrors agent-compatibility.ts. No API routes — derived client-side.

export const AGENT_SUGGESTED_ACTIONS = [
  'proceed', 'retry_later', 'downgrade_effect', 'change_environment',
  'await_oversight', 'provide_credential', 'contact_operator', 'investigate',
] as const;
export type AgentSuggestedAction = typeof AGENT_SUGGESTED_ACTIONS[number];

export interface AgentDecisionProjection {
  actionable: boolean;
  posture: ActionPosture;
  retryable: boolean;
  alternative_available: boolean;
  reason_code: string;
  decided_in_phase: string | null;
  suggested_action: AgentSuggestedAction;
}

export interface AgentArtifactReferences {
  execution_id: string | null;
  correlation_id: string;
  precursor_hash: string | null;
  transcript_hash: string | null;
  oversight_request_id: string | null;
  monitoring_bridge_id: string | null;
}

export interface AgentTrustProjection {
  trust_score: number;
  trust_delta: number;
  trust_direction: 'increased' | 'decreased' | 'unchanged';
  authority_granted: boolean;
  authority_exceeded: boolean;
}

export interface WorkflowInvocationProjection {
  step_id: string;
  correlation_id: string;
  step_completed: boolean;
  step_blocked: boolean;
  requires_human: boolean;
  workflow_continuable: boolean;
  runtime_profile: RuntimeProfile;
  effective_ceiling: ActionClassCeiling;
  environment: Environment;
}

export interface WorkflowGuardProjection {
  policy_passed: boolean;
  guards_passed: boolean;
  credential_bound: boolean;
  oversight_clear: boolean;
  degradation_clear: boolean;
  all_clear: boolean;
}

export const TOOL_GOVERNANCE_MODES = [
  'fully_governed', 'credential_governed', 'oversight_governed', 'degraded', 'ungoverned',
] as const;
export type ToolGovernanceMode = typeof TOOL_GOVERNANCE_MODES[number];

export interface ToolAdapterCompatibility {
  governance_mode: ToolGovernanceMode;
  enforcement_chain_length: number;
  guard_chain_length: number;
  supports_credential_injection: boolean;
  supports_oversight_gating: boolean;
  supports_degradation_handling: boolean;
  max_timeout_ms: number;
  allowed_methods: string[];
}

/**
 * Derive agent decision projection from execution data.
 * Mirrors buildAgentDecisionProjection + deriveSuggestedAction.
 */
export function deriveAgentDecisionProjection(
  exec: ExecutionSummary,
  explanation: CanonicalExplanation | null,
): AgentDecisionProjection {
  const decision = explanation?.decision || 'failed';
  const posture: ActionPosture = decision === 'allowed' ? 'executable'
    : decision === 'blocked' && (explanation?.downgrade_options?.length ?? 0) > 0 ? 'downgradeable'
    : decision === 'blocked' ? 'blocked' : 'failed';
  const retryable = exec.status === 'timed_out' || exec.status === 'failed';
  const altAvail = (explanation?.downgrade_options?.length ?? 0) > 0 || !!explanation?.next_safe_action;
  const rc = exec.reason_code || explanation?.decision_code || 'execution_success';

  let suggested: AgentSuggestedAction;
  if (posture === 'executable') suggested = 'proceed';
  else if (exec.status === 'pending_oversight') suggested = 'await_oversight';
  else if (rc.startsWith('credential_')) suggested = 'provide_credential';
  else if (retryable) suggested = 'retry_later';
  else if (posture === 'downgradeable') {
    suggested = rc === 'action_ceiling_exceeded' || rc === 'environment_ineligible'
      ? 'change_environment' : 'downgrade_effect';
  }
  else if (posture === 'failed') suggested = 'investigate';
  else suggested = 'contact_operator';

  return {
    actionable: posture === 'executable',
    posture,
    retryable,
    alternative_available: altAvail,
    reason_code: rc,
    decided_in_phase: exec.reason_phase || null,
    suggested_action: suggested,
  };
}

/**
 * Derive workflow guard projection from execution data.
 * Mirrors buildWorkflowGuardProjection.
 */
export function deriveWorkflowGuardProjection(
  exec: ExecutionSummary,
  explanation: CanonicalExplanation | null,
): WorkflowGuardProjection {
  const decision = explanation?.decision;
  const policyPassed = decision !== 'blocked' || !exec.reason_phase || exec.reason_phase !== 'policy';
  const guardsPassed = decision !== 'blocked' || exec.reason_phase !== 'guard';
  const credentialBound = !exec.reason_code?.startsWith('credential_');
  const oversightClear = exec.status !== 'pending_oversight';
  const degradationClear = true; // no degradation_blocked info in summary

  return {
    policy_passed: policyPassed,
    guards_passed: guardsPassed,
    credential_bound: credentialBound,
    oversight_clear: oversightClear,
    degradation_clear: degradationClear,
    all_clear: policyPassed && guardsPassed && credentialBound && oversightClear && degradationClear,
  };
}

/** Minimal export fields for external exchange */
export const MINIMAL_EXPORT_FIELDS = [
  'event_id', 'correlation_id', 'status', 'category', 'severity', 'occurred_at',
] as const;

/** Export-safe field allowlist (matches replay-support.ts EXPORT_SAFE_FIELDS) */
export const EXPORT_SAFE_FIELDS = [
  'event_id', 'correlation_id', 'category', 'severity', 'status',
  'reason_code', 'decided_in_phase', 'object_id', 'capability_id',
  'environment', 'requested_effect', 'granted_effect', 'trust_score',
  'trust_delta', 'duration_ms', 'occurred_at',
] as const;

// ── Server-Backed Replay + Export ───────────────────────────────────────────

export interface ReplayEnhancedResponse {
  replay_record: RuntimeReplayRecord;
}

export interface ExportArtifactEntry {
  artifact: Record<string, unknown>;
  safety: {
    safe: boolean;
    warnings: string[];
  };
}

export type ExportAuditStatus = 'logged' | 'persisted' | 'not_tracked';

export interface ExportMetadataEnvelope {
  exported_at: string;
  export_mode: string;
  exported_by: string | null;
  correlation_id: string;
  record_count: number;
  protection: ExportProtectionSummary;
  audit_status: ExportAuditStatus;
}

export interface InvestigationSummary {
  correlation_id: string;
  event_count: number;
  distinct_objects: string[];
  distinct_capabilities: string[];
  environments: string[];
  time_span: { earliest: string; latest: string } | null;
  severity_counts: Record<string, number>;
  category_counts: Record<string, number>;
  has_invalidation: boolean;
  has_oversight: boolean;
  has_degradation: boolean;
  has_credential_events: boolean;
  refresh_required: boolean;
}

export interface CorrelationExportResponse {
  export_metadata: ExportMetadataEnvelope;
  correlation_summary: ReplayCorrelationProjection | null;
  investigation: InvestigationSummary;
  export_artifacts: ExportArtifactEntry[];
}

/** Server-backed enhanced replay (taxonomy computed server-side) */
export async function getReplayEnhanced(executionId: string): Promise<ReplayEnhancedResponse> {
  const response = await fetch(`${PLATFORM_BASE}/admin/executions/replay-enhanced/${encodeURIComponent(executionId)}`, {
    headers: adminHeaders(),
  });
  return handleResponse<ReplayEnhancedResponse>(response);
}

/** Server-backed audit export bundle (safety-validated, correlation-projected) */
export async function getCorrelationExport(correlationId: string, mode?: string): Promise<CorrelationExportResponse> {
  const params = mode ? `?mode=${encodeURIComponent(mode)}` : '';
  const response = await fetch(`${PLATFORM_BASE}/admin/executions/export/${encodeURIComponent(correlationId)}${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<CorrelationExportResponse>(response);
}

// ── Runtime Verification Summary (Server-Backed) ────────────────────────────

export interface RuntimeIntegritySummary {
  total_artifacts: number;
  hash_linked_count: number;
  signed_count: number;
  unsigned_count: number;
  integrity_checked_count: number;
  signing_available: boolean;
}

export interface VerificationSummaryResponse {
  boundary: RuntimeBoundarySummary;
  integrity: RuntimeIntegritySummary;
  signing: {
    available: boolean;
    algorithm: string | null;
    issuer: string;
  };
  sampled_executions: number;
}

// ── Oversight Types ─────────────────────────────────────────────────────────

export type OversightRequestStatus = 'pending' | 'approved' | 'denied' | 'expired' | 'escalated';
export type OversightDecisionType = 'approve' | 'deny' | 'approve_with_conditions';

/** Enriched oversight request — matches the flattened T1 list contract */
export interface OversightRequestSummary {
  request_id: string;
  status: OversightRequestStatus;
  capability_id: string;
  environment: string;
  object_id: string | null;
  principal_id: string | null;
  trigger_policy_id: string;
  deadline: string | null;
  reviewed_at: string | null;
  reviewer_id: string | null;
  review_notes: string | null;
  created_at: string;
  // Risk context
  risk_tier: number | null;
  // Agent context
  agent_display_name: string | null;
  agent_lifecycle_status: string | null;
  agent_trust_score: number | null;
  agent_trust_state: string | null;
  // Execution closure
  retried: boolean;
  execution_status: string | null;
  execution_outcome_class: string | null;
  execution_duration_ms: number | null;
  execution_completed_at: string | null;
  execution_error: string | null;
  execution_mode: string | null;
  // Activation guidance
  activation_required: boolean;
  // Reviewer identity
  reviewer_display_name: string | null;
  // Drilldown identifiers
  correlation_id: string | null;
  tool_id: string | null;
  // Operator guidance
  operator_guidance: string | null;
  // Legacy fields (may still appear on older records)
  risk_summary?: Record<string, unknown> | null;
  expires_at?: string | null;
  resolved_at?: string | null;
  decision_hash?: string | null;
  trigger_rule_id?: string;
  tenant_id?: string | null;
  risk_level?: string;
}

/** Detail-enriched oversight request (from GET /oversight/:requestId) */
export interface OversightRequestDetail extends OversightRequestSummary {
  agent_context?: {
    display_name: string | null;
    lifecycle_status: string | null;
    trust_score: number | null;
    trust_state: string | null;
  } | null;
  execution_context?: {
    retried: boolean;
    latest_decision: string | null;
    latest_decision_code: string | null;
    latest_correlation_id: string | null;
    latest_decision_at: string | null;
    execution_id: string | null;
    execution_status: string | null;
    execution_completed_at: string | null;
    execution_error: string | null;
    execution_duration_ms: number | null;
    execution_outcome_class: string | null;
  } | null;
}

export interface OversightDecisionResult {
  request: OversightRequestSummary;
  decision_hash: string;
}

const OVERSIGHT_BASE = `${BASE_URL}/api/v1/oversight`;

/** List oversight requests with optional status filter */
export async function listOversightPending(filters: {
  tenant_id?: string;
  environment?: string;
  object_id?: string;
  limit?: number;
  status?: 'pending' | 'resolved' | 'all';
} = {}): Promise<{ requests: OversightRequestSummary[] }> {
  const params = new URLSearchParams();
  if (filters.tenant_id) params.set('tenant_id', filters.tenant_id);
  if (filters.environment) params.set('environment', filters.environment);
  if (filters.object_id) params.set('object_id', filters.object_id);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.status) params.set('status', filters.status);

  const response = await fetch(`${OVERSIGHT_BASE}/pending?${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<{ requests: OversightRequestSummary[] }>(response);
}

/** Get oversight request detail (enriched with agent_context, execution_context, operator_guidance) */
export async function getOversightRequest(requestId: string): Promise<OversightRequestDetail> {
  const response = await fetch(`${OVERSIGHT_BASE}/${encodeURIComponent(requestId)}`, {
    headers: adminHeaders(),
  });
  return handleResponse<OversightRequestDetail>(response);
}

/** Submit oversight decision */
export async function decideOversightRequest(
  requestId: string,
  decision: OversightDecisionType,
  notes: string,
  conditions?: Array<{ type: string; parameters: Record<string, unknown> }>,
): Promise<OversightDecisionResult> {
  const body: Record<string, unknown> = { decision, notes };
  if (conditions) body.conditions = conditions;

  const response = await fetch(`${OVERSIGHT_BASE}/${encodeURIComponent(requestId)}/decide`, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse<OversightDecisionResult>(response);
}

/** Expire stale oversight requests */
export async function expireOversightRequests(): Promise<{ expired_count: number }> {
  const response = await fetch(`${OVERSIGHT_BASE}/expire`, {
    method: 'POST',
    headers: adminHeaders(),
  });
  return handleResponse<{ expired_count: number }>(response);
}

/** Trigger learning measurement run */
export async function triggerMeasurementRun(): Promise<Record<string, unknown>> {
  const response = await fetch(`${BASE_URL}/api/v1/learning/measurement/run`, {
    method: 'POST',
    headers: adminHeaders(),
  });
  return handleResponse<Record<string, unknown>>(response);
}

// ── Proof-Carrying Action Rights (PCAR) ─────────────────────────────────────

export type RevocationCheckMode = 'none' | 'check_dependency' | 'check_policy' | 'check_trust' | 'check_all';
export type PortabilityLevel = 'local_only' | 'cross_system' | 'external';

export interface ActionRightScope {
  effect_ceiling: ActionClassCeiling;
  ceiling_rank: number;
  environment: Environment;
  object_id: string | null;
  capability_id: string | null;
  tenant_id: string | null;
}

export interface ActionRightDependencySet {
  capability_pins: Array<{
    capability_id: string;
    contract_hash: string;
    capability_version: number | null;
  }>;
  policy_set_id: string | null;
  trust_score_at_issuance: number;
  transcript_hash: string | null;
}

export interface ActionRightValiditySummary {
  validity: PrecursorValidity;
  precursor_status: PrecursorStatus;
  issued_at: string;
  expires_at: string | null;
  integrity_verified: boolean;
  correlation_consistent: boolean;
}

export interface ActionRightRevocationHook {
  check_mode: RevocationCheckMode;
  dependency_ids: string[];
  policy_set_id: string | null;
  trust_floor: number;
}

export interface ProofCarryingActionRight {
  right_id: string;
  pcar_version: string;
  issuer: string;
  deployment_mode: string;
  decision: Decision;
  decision_code: string;
  scope: ActionRightScope;
  dependencies: ActionRightDependencySet;
  validity: ActionRightValiditySummary;
  revocation: ActionRightRevocationHook;
  portability: PortabilityLevel;
  precursor_hash: string;
  right_hash: string;
  correlation_id: string;
  assembled_at: string;
}

export interface VerifierInputProjection {
  right_hash: string;
  precursor_hash: string;
  issuer: string;
  deployment_mode: string;
  valid_from: string;
  valid_until: string | null;
  revocation_check: RevocationCheckMode;
  dependency_check_ids: string[];
  trust_floor: number;
  integrity_verified: boolean;
  signed: boolean;
}

export interface WarrantPrecursor {
  artifact_type: 'warrant_precursor';
  right: ProofCarryingActionRight;
  verifier_input: VerifierInputProjection;
  packaged_at: string;
}

export interface ExecutionRightResponse {
  right: ProofCarryingActionRight | null;
  reason?: string;
  warrant_precursor?: WarrantPrecursor;
  signing_available?: boolean;
}

/** Get proof-carrying action right for an execution */
export async function getExecutionRight(executionId: string, includeWarrant = false): Promise<ExecutionRightResponse> {
  const params = includeWarrant ? '?warrant=true' : '';
  const response = await fetch(`${PLATFORM_BASE}/admin/executions/${encodeURIComponent(executionId)}/right${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<ExecutionRightResponse>(response);
}

// ── Credential Governance Types ──────────────────────────────────────────────

export interface CredentialSummary {
  credential_id: string;
  principal_id: string;
  credential_type: string;
  display_name: string;
  provider: string;
  capability_scope: string[];
  status: string;
  expires_at: string | null;
  rotation_due_at: string | null;
  tenant_id: string | null;
  created_at: string;
}

export interface CredentialBinding {
  binding_id: string;
  credential_id: string;
  object_id: string;
  capability_id: string;
  principal_id: string;
  status: string;
  tenant_id: string | null;
  bound_at: string;
}

export interface CredentialUsageEntry {
  usage_id: string;
  credential_id: string;
  binding_id: string;
  used_at: string;
  success: boolean;
}

const CREDENTIALS_BASE = `${BASE_URL}/api/v1/credentials`;

/** List credentials for a principal */
export async function listCredentials(principalId?: string, tenantId?: string): Promise<{ credentials: CredentialSummary[] }> {
  const params = new URLSearchParams();
  if (principalId) params.set('principal_id', principalId);
  if (tenantId) params.set('tenant_id', tenantId);

  const response = await fetch(`${CREDENTIALS_BASE}?${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<{ credentials: CredentialSummary[] }>(response);
}

/** List credential bindings */
export async function listCredentialBindings(objectId?: string, principalId?: string): Promise<{ bindings: CredentialBinding[] }> {
  const params = new URLSearchParams();
  if (objectId) params.set('object_id', objectId);
  if (principalId) params.set('principal_id', principalId);

  const response = await fetch(`${CREDENTIALS_BASE}/bindings?${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<{ bindings: CredentialBinding[] }>(response);
}

/** Get credential usage log */
export async function getCredentialUsage(credentialId: string, limit = 50): Promise<{ usage: CredentialUsageEntry[] }> {
  const response = await fetch(`${CREDENTIALS_BASE}/${encodeURIComponent(credentialId)}/usage?limit=${limit}`, {
    headers: adminHeaders(),
  });
  return handleResponse<{ usage: CredentialUsageEntry[] }>(response);
}

// ── Notification Rules ──────────────────────────────────────────────────────

export interface NotificationRule {
  rule_id: string;
  event_pattern: string;
  channels: Array<{ type: string; endpoint: string }>;
  created_by: string;
  tenant_id: string | null;
  created_at: string;
}

/** List notification rules */
export async function listNotificationRules(tenantId?: string): Promise<{ rules: NotificationRule[] }> {
  const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
  const response = await fetch(`${BASE_URL}/api/v1/notifications/rules${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<{ rules: NotificationRule[] }>(response);
}

/** Store a new credential */
export async function storeCredential(data: {
  principal_id: string;
  credential_type: string;
  display_name: string;
  provider: string;
  plaintext_value: string;
  capability_scope: string[];
  expires_at?: string;
  tenant_id?: string;
}): Promise<{ credential_id: string }> {
  const response = await fetch(`${CREDENTIALS_BASE}`, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<{ credential_id: string }>(response);
}

/** Create a credential binding */
export async function createCredentialBinding(data: {
  credential_id: string;
  object_id: string;
  capability_id: string;
  principal_id: string;
  tenant_id?: string;
}): Promise<{ binding_id: string }> {
  const response = await fetch(`${CREDENTIALS_BASE}/bindings`, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<{ binding_id: string }>(response);
}

/** Unbind a credential from an object */
export async function unbindCredential(bindingId: string): Promise<{ unbound: boolean }> {
  const response = await fetch(`${CREDENTIALS_BASE}/bindings/${encodeURIComponent(bindingId)}`, {
    method: 'DELETE',
    headers: adminHeaders(),
  });
  return handleResponse<{ unbound: boolean }>(response);
}

/** Create a notification rule */
export async function createNotificationRule(data: {
  event_pattern: string;
  channels: Array<{ type: string; endpoint: string }>;
  tenant_id?: string;
}): Promise<{ rule_id: string }> {
  const response = await fetch(`${BASE_URL}/api/v1/notifications/rules`, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<{ rule_id: string }>(response);
}

/** Delete a notification rule */
export async function deleteNotificationRule(ruleId: string): Promise<{ deleted: boolean }> {
  const response = await fetch(`${BASE_URL}/api/v1/notifications/rules/${encodeURIComponent(ruleId)}`, {
    method: 'DELETE',
    headers: adminHeaders(),
  });
  return handleResponse<{ deleted: boolean }>(response);
}

// ── Incident + Degradation Types ─────────────────────────────────────────────

export type IncidentSeverity = 'critical' | 'high' | 'medium' | 'low';
export type IncidentTriggerType = 'manual' | 'automated_detection' | 'oversight_escalation' | 'trust_collapse';
export type DegradationMode =
  | 'oversight_mandatory' | 'read_only_degrade' | 'no_execute_degrade'
  | 'suspend_object' | 'suspend_capability' | 'suspend_environment'
  | 'suspend_tenant' | 'policy_override_freeze' | 'signature_disable'
  | 'delegation_shutdown';

export interface IncidentSummary {
  incident_id: string;
  severity: IncidentSeverity;
  status: 'open' | 'investigating' | 'resolved';
  title: string;
  description?: string;
  trigger_type: IncidentTriggerType;
  trigger_source_id?: string;
  affected_objects?: string[];
  blast_radius?: Record<string, unknown>;
  opened_by: string;
  resolved_at?: string;
  resolution_notes?: string;
  tenant_id?: string;
  created_at: string;
}

export interface DegradationSummary {
  degradation_id: string;
  incident_id?: string;
  mode: DegradationMode;
  scope: Record<string, unknown>;
  target_id?: string;
  reason?: string;
  applied_by: string;
  removed_at?: string;
  tenant_id?: string;
  created_at: string;
}

const INCIDENTS_BASE = `${BASE_URL}/api/v1/incidents`;

/** List incidents */
export async function listIncidents(filters?: {
  status?: string;
  severity?: string;
  tenant_id?: string;
  limit?: number;
}): Promise<{ incidents: IncidentSummary[] }> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.severity) params.set('severity', filters.severity);
  if (filters?.tenant_id) params.set('tenant_id', filters.tenant_id);
  if (filters?.limit) params.set('limit', String(filters.limit));

  const response = await fetch(`${INCIDENTS_BASE}?${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<{ incidents: IncidentSummary[] }>(response);
}

/** Open a new incident */
export async function openIncident(data: {
  severity: IncidentSeverity;
  title: string;
  description?: string;
  trigger_type: IncidentTriggerType;
  trigger_source_id?: string;
  affected_objects?: string[];
  tenant_id?: string;
}): Promise<IncidentSummary> {
  const response = await fetch(`${INCIDENTS_BASE}`, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<IncidentSummary>(response);
}

/** Resolve an incident */
export async function resolveIncident(incidentId: string, notes: string): Promise<IncidentSummary> {
  const response = await fetch(`${INCIDENTS_BASE}/${encodeURIComponent(incidentId)}/resolve`, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify({ notes }),
  });
  return handleResponse<IncidentSummary>(response);
}

/** Get active degradations */
export async function getActiveDegradations(tenantId?: string): Promise<{ degradations: DegradationSummary[] }> {
  const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
  const response = await fetch(`${INCIDENTS_BASE}/degradations${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse<{ degradations: DegradationSummary[] }>(response);
}

/** Apply a degradation */
export async function applyDegradation(data: {
  incident_id?: string;
  mode: DegradationMode;
  scope?: Record<string, unknown>;
  target_id?: string;
  reason?: string;
  tenant_id?: string;
}): Promise<DegradationSummary> {
  const response = await fetch(`${INCIDENTS_BASE}/degradations`, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<DegradationSummary>(response);
}

/** Remove a degradation */
export async function removeDegradation(degradationId: string): Promise<{ removed: boolean }> {
  const response = await fetch(`${INCIDENTS_BASE}/degradations/${encodeURIComponent(degradationId)}`, {
    method: 'DELETE',
    headers: adminHeaders(),
  });
  return handleResponse<{ removed: boolean }>(response);
}

// ── Intelligence / Self-Improving Registry ───────────────────────────────────

// Intelligence types are loosely typed here because the backend response
// shapes vary (source_type vs source_id, by_tier vs indexed_tools, etc.).
// IntelligenceView normalizes at the boundary before rendering.

/** Get per-source crawl performance analytics */
export async function getSourceStats(): Promise<{ source_stats: Record<string, unknown>[]; count: number }> {
  const response = await fetch(`${BASE_URL}/api/v1/admin/intelligence/source-stats`, {
    headers: adminHeaders(),
  });
  return handleResponse(response);
}

/** Get registry snapshots (default 7 days) */
export async function getRegistrySnapshots(days = 7): Promise<{ snapshots: Record<string, unknown>[]; count: number }> {
  const response = await fetch(`${BASE_URL}/api/v1/admin/intelligence/snapshots?days=${days}`, {
    headers: adminHeaders(),
  });
  return handleResponse(response);
}

/** Get enrichment feedback summary */
export async function getEnrichmentFeedback(days = 7): Promise<{ enrichment_feedback: Record<string, unknown> }> {
  const response = await fetch(`${BASE_URL}/api/v1/admin/intelligence/enrichment-feedback?days=${days}`, {
    headers: adminHeaders(),
  });
  return handleResponse(response);
}

/** Server-backed runtime verification summary (admin) */
export async function getVerificationSummary(): Promise<VerificationSummaryResponse> {
  const response = await fetch(`${PLATFORM_BASE}/admin/runtime/verification-summary`, {
    headers: adminHeaders(),
  });
  return handleResponse<VerificationSummaryResponse>(response);
}

// ── Onboarding API ──────────────────────────────────────────────────────────

const ONBOARDING_BASE = `${BASE_URL}/api/v1/onboarding`;
const GOVERNANCE_BASE = `${BASE_URL}/api/v1/governance`;

export async function listOnboardingTemplates(): Promise<any> {
  const response = await fetch(`${ONBOARDING_BASE}/templates`);
  return handleResponse(response);
}

export async function createOnboardingDraft(body: { template_id?: string; draft_data?: any }): Promise<any> {
  const response = await fetch(`${ONBOARDING_BASE}/drafts`, {
    method: 'POST',
    headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handleResponse(response);
}

export async function listOnboardingDrafts(params?: { status?: string; limit?: number }): Promise<any> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.limit) qs.set('limit', String(params.limit));
  const qstr = qs.toString();
  const response = await fetch(`${ONBOARDING_BASE}/drafts${qstr ? `?${qstr}` : ''}`, {
    headers: adminHeaders(),
  });
  return handleResponse(response);
}

export async function getOnboardingDraft(id: string): Promise<any> {
  const response = await fetch(`${ONBOARDING_BASE}/drafts/${encodeURIComponent(id)}`, {
    headers: adminHeaders(),
  });
  return handleResponse(response);
}

export async function updateOnboardingDraft(id: string, body: { step?: string; draft_data?: any }): Promise<any> {
  const response = await fetch(`${ONBOARDING_BASE}/drafts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handleResponse(response);
}

export async function generateOnboardingContract(id: string): Promise<any> {
  const response = await fetch(`${ONBOARDING_BASE}/drafts/${encodeURIComponent(id)}/generate-contract`, {
    method: 'POST',
    headers: adminHeaders(),
  });
  return handleResponse(response);
}

export async function submitOnboardingDraft(id: string): Promise<any> {
  const response = await fetch(`${ONBOARDING_BASE}/drafts/${encodeURIComponent(id)}/submit`, {
    method: 'POST',
    headers: adminHeaders(),
  });
  return handleResponse(response);
}

export async function abandonOnboardingDraft(id: string): Promise<any> {
  const response = await fetch(`${ONBOARDING_BASE}/drafts/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: adminHeaders(),
  });
  return handleResponse(response);
}

export async function testOnboardingDraft(id: string, testInput?: Record<string, any>): Promise<any> {
  const response = await fetch(`${ONBOARDING_BASE}/drafts/${encodeURIComponent(id)}/test`, {
    method: 'POST',
    headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ test_input: testInput || {} }),
  });
  return handleResponse(response);
}

export async function listGovernanceProfiles(): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/profiles`);
  return handleResponse(response);
}

// ── Bootstrap Token Admin ────────────────────────────────────────────────────

const ADMIN_BASE = `${BASE_URL}/api/v1/admin`;

export async function createBootstrapToken(body: {
  tenant_id: string;
  label?: string;
  effect_ceiling?: string;
  max_uses?: number;
  expires_in_hours?: number;
  capability_domain?: string;
}): Promise<any> {
  const response = await fetch(`${ADMIN_BASE}/bootstrap-tokens`, {
    method: 'POST',
    headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handleResponse(response);
}

export async function listBootstrapTokens(tenantId?: string): Promise<any> {
  const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
  const response = await fetch(`${ADMIN_BASE}/bootstrap-tokens${params}`, {
    headers: adminHeaders(),
  });
  return handleResponse(response);
}

export async function revokeBootstrapToken(tokenId: string): Promise<any> {
  const response = await fetch(`${ADMIN_BASE}/bootstrap-tokens/${encodeURIComponent(tokenId)}`, {
    method: 'DELETE',
    headers: adminHeaders(),
  });
  return handleResponse(response);
}

// ── Control Tower API ─────────────────────────────────────────────────────────

const TOWER_BASE = `${BASE_URL}/api/v1/control-tower`;

export async function getControlTowerSummary(tenantId?: string): Promise<any> {
  const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
  const response = await fetch(`${TOWER_BASE}/summary${params}`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function getAdmissionsInbox(params?: { tenant_id?: string; limit?: number; offset?: number }): Promise<any> {
  const qs = new URLSearchParams();
  if (params?.tenant_id) qs.set('tenant_id', params.tenant_id);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  const qstr = qs.toString();
  const response = await fetch(`${TOWER_BASE}/inbox${qstr ? `?${qstr}` : ''}`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function getLifecycleCard(objectId: string): Promise<any> {
  const response = await fetch(`${TOWER_BASE}/lifecycle/${encodeURIComponent(objectId)}`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function getPromotionQueue(tenantId?: string): Promise<any> {
  const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
  const response = await fetch(`${TOWER_BASE}/promotion-queue${params}`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function getGlobalAlerts(): Promise<any> {
  const response = await fetch(`${TOWER_BASE}/alerts`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function getObjectAlerts(objectId: string): Promise<any> {
  const response = await fetch(`${TOWER_BASE}/alerts/${encodeURIComponent(objectId)}`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function getApiKeyStatuses(): Promise<any> {
  const response = await fetch(`${TOWER_BASE}/keys`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function renewApiKey(keyId: string, extendsDays?: number): Promise<any> {
  const response = await fetch(`${TOWER_BASE}/keys/${encodeURIComponent(keyId)}/renew`, {
    method: 'POST',
    headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ extends_days: extendsDays || 30 }),
  });
  return handleResponse(response);
}

// ── Production Readiness ─────────────────────────────────────────────────────

export async function getProductionReadiness(objectId: string): Promise<any> {
  const response = await fetch(`${PLATFORM_BASE}/${encodeURIComponent(objectId)}/production-readiness`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function promoteProductionReadOnly(objectId: string, reason: string): Promise<any> {
  const response = await fetch(`${PLATFORM_BASE}/${encodeURIComponent(objectId)}/promote-production-readonly`, {
    method: 'POST',
    headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return handleResponse(response);
}

// ── Governance Mutation + History ─────────────────────────────────────────────

export async function mutateGovernance(objectId: string, body: {
  action_class_ceiling?: string;
  environment_eligibility?: Record<string, boolean>;
  isolation_profile?: string;
  reason?: string;
}): Promise<any> {
  const response = await fetch(`${PLATFORM_BASE}/${encodeURIComponent(objectId)}/governance`, {
    method: 'PATCH',
    headers: adminHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
}

export async function getGovernanceHistory(objectId: string, limit = 50): Promise<any> {
  const response = await fetch(`${PLATFORM_BASE}/${encodeURIComponent(objectId)}/governance-history?limit=${limit}`, {
    headers: adminHeaders(),
  });
  return handleResponse(response);
}

// ── Governance Packs ────────────────────────────────────────────────────────

export async function getEffectivePolicy(): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/effective-policy`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function getStarterPacks(): Promise<{ packs: Array<{ id: string; name: string; description: string }> }> {
  const response = await fetch(`${GOVERNANCE_BASE}/starter-packs`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function previewPackImpact(starterPackId: string): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/packs/preview-impact?starter_pack_id=${encodeURIComponent(starterPackId)}`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function activateStarterPack(starterPackId: string, rationale?: string): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/packs/activate`, {
    method: 'POST', headers: adminHeaders(),
    body: JSON.stringify({ starter_pack_id: starterPackId, rationale: rationale || `Activated ${starterPackId} pack` }),
  });
  return handleResponse(response);
}

export async function createDraftPack(source: string, starterPackId?: string, contentOverrides?: Record<string, unknown>, rationale?: string, domainPackId?: string | null): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/packs/draft`, {
    method: 'POST', headers: adminHeaders(),
    body: JSON.stringify({ source, starter_pack_id: starterPackId, content_overrides: contentOverrides, rationale, domain_pack_id: domainPackId ?? undefined }),
  });
  return handleResponse(response);
}

export async function updateDraftPack(packId: string, content?: Record<string, unknown>, domainPackId?: string | null): Promise<any> {
  const body: Record<string, unknown> = {};
  if (content) body.content = content;
  if (domainPackId !== undefined) body.domain_pack_id = domainPackId;
  const response = await fetch(`${GOVERNANCE_BASE}/packs/${encodeURIComponent(packId)}`, {
    method: 'PATCH', headers: adminHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
}

export async function validatePack(packId: string): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/packs/${encodeURIComponent(packId)}/validate`, {
    method: 'POST', headers: adminHeaders(),
  });
  return handleResponse(response);
}

export async function activateDraftPack(packId: string, rationale?: string): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/packs/${encodeURIComponent(packId)}/activate`, {
    method: 'POST', headers: adminHeaders(),
    body: JSON.stringify({ rationale }),
  });
  return handleResponse(response);
}

export async function rollbackPack(rationale?: string): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/packs/rollback`, {
    method: 'POST', headers: adminHeaders(),
    body: JSON.stringify({ rationale }),
  });
  return handleResponse(response);
}

// ── Governance Programs (Phase 11) ──────────────────────────────────────────

export async function listGovernancePrograms(): Promise<{ programs: any[]; total: number }> {
  const response = await fetch(`${GOVERNANCE_BASE}/programs`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function getGovernanceProgram(programId: string): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/programs/${encodeURIComponent(programId)}`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function createGovernanceProgram(body: { name: string; description?: string; fallback_pack_id?: string }): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/programs`, {
    method: 'POST', headers: adminHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
}

export async function addProgramBinding(programId: string, body: {
  pack_id: string; label?: string | null;
  scope_agent_classes?: string[] | null; scope_environments?: string[] | null;
  scope_workflow_types?: string[] | null; scope_business_units?: string[] | null;
  is_default?: boolean; priority?: number;
}): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/programs/${encodeURIComponent(programId)}/bindings`, {
    method: 'POST', headers: adminHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
}

export async function deleteProgramBinding(programId: string, bindingId: string): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/programs/${encodeURIComponent(programId)}/bindings/${encodeURIComponent(bindingId)}`, {
    method: 'DELETE', headers: adminHeaders(),
  });
  return handleResponse(response);
}

export async function activateProgram(programId: string, rationale: string): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/programs/${encodeURIComponent(programId)}/activate`, {
    method: 'POST', headers: adminHeaders(),
    body: JSON.stringify({ rationale }),
  });
  if (response.status === 422) {
    const data = await response.json();
    return { _validationError: true, ...data };
  }
  return handleResponse(response);
}

export async function deactivateProgram(programId: string, rationale: string): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/programs/${encodeURIComponent(programId)}/deactivate`, {
    method: 'POST', headers: adminHeaders(),
    body: JSON.stringify({ rationale }),
  });
  return handleResponse(response);
}

export async function resolveProgram(programId: string, context: {
  agent_class?: string; environment?: string; workflow_type?: string; business_unit?: string;
}): Promise<any> {
  const response = await fetch(`${GOVERNANCE_BASE}/programs/${encodeURIComponent(programId)}/resolve`, {
    method: 'POST', headers: adminHeaders(),
    body: JSON.stringify(context),
  });
  return handleResponse(response);
}

// ── Onboarding Setup ────────────────────────────────────────────────────────

export async function getOnboardingStatus(): Promise<any> {
  const response = await fetch(`${BASE_URL}/api/v1/onboarding/status`, { headers: adminHeaders() });
  return handleResponse(response);
}

export async function runOnboardingSetup(params: { starter_pack_id?: string; agent_name: string; agent_description?: string }): Promise<any> {
  const response = await fetch(`${BASE_URL}/api/v1/onboarding/setup`, {
    method: 'POST', headers: adminHeaders(),
    body: JSON.stringify(params),
  });
  return handleResponse(response);
}

export const platformApi = {
  listExecutions,
  getExecutionDetail,
  getExecutionByCorrelation,
  getExecutionReplay,
  getExecutionPrecursor,
  getPrecursorByCorrelation,
  getDecisionStats,
  getBlockReasons,
  getTrustHistory,
  listPlatformObjects,
  getPlatformObject,
  suspendObject,
  evaluatePlatformObject,
  activatePlatformObject,
  listRecommendations,
  getRecommendationQueueSummary,
  getRecommendationDetail,
  decideRecommendation,
  queueRecommendation,
  getRecommendationAudit,
  getRecommendationBridge,
  getRecommendationEfficacy,
  getRecommendationSupersessionChain,
  applyRecommendation,
  getReplayEnhanced,
  getCorrelationExport,
  getVerificationSummary,
  getApplicationAudit,
  getExecutionRight,
  listOversightPending,
  getOversightRequest,
  decideOversightRequest,
  expireOversightRequests,
  triggerMeasurementRun,
  listCredentials,
  listCredentialBindings,
  getCredentialUsage,
  storeCredential,
  createCredentialBinding,
  unbindCredential,
  listNotificationRules,
  createNotificationRule,
  deleteNotificationRule,
  listIncidents,
  openIncident,
  resolveIncident,
  getActiveDegradations,
  applyDegradation,
  removeDegradation,
  getSourceStats,
  getRegistrySnapshots,
  getEnrichmentFeedback,
  getAdminToken,
  setAdminToken,
  clearAdminToken,
  // Onboarding
  listOnboardingTemplates,
  createOnboardingDraft,
  listOnboardingDrafts,
  getOnboardingDraft,
  updateOnboardingDraft,
  generateOnboardingContract,
  submitOnboardingDraft,
  abandonOnboardingDraft,
  testOnboardingDraft,
  listGovernanceProfiles,
  createBootstrapToken,
  listBootstrapTokens,
  revokeBootstrapToken,
  // Control Tower
  getControlTowerSummary,
  getAdmissionsInbox,
  getLifecycleCard,
  getPromotionQueue,
  getGlobalAlerts,
  getObjectAlerts,
  getApiKeyStatuses,
  renewApiKey,
  getProductionReadiness,
  promoteProductionReadOnly,
  mutateGovernance,
  getGovernanceHistory,
  // Governance Packs
  getEffectivePolicy,
  getStarterPacks,
  previewPackImpact,
  activateStarterPack,
  createDraftPack,
  updateDraftPack,
  validatePack,
  activateDraftPack,
  rollbackPack,
  // Onboarding Setup
  getOnboardingStatus,
  runOnboardingSetup,
};
